class InfiniteSlider {
    constructor(container, options = {}) {
        this.container = container;
        this.track = container.querySelector('.arcanov-slider__row');
        this.sliderId = container.getAttribute('slider-id');
        
        // Get styles from data-style
        const styleData = container.getAttribute('data-style');
        this.styleConfig = styleData ? JSON.parse(styleData) : {};
        
        // Get configuration from data-config
        const configData = container.getAttribute('data-config');
        let config = {};
        if (configData) {
            try {
                config = JSON.parse(configData);
            } catch (e) {
                console.warn('Invalid slider config JSON:', configData);
            }
        }
        
        this.originalItems = Array.from(this.track.children);
        if (this.originalItems.length === 0) {
            console.error('Slider initialization failed: no slides found');
            return;
        }
        
        this.itemWidth = this.calculateItemWidth();
        
        this.currentOffset = 0;
        this.baseOffset = 0;
        this.isDragging = false;
        this.startX = 0;
        this.startY = 0;
        this.startOffset = 0;
        
        // Click blocking properties
        this.dragDistance = 0;
        this.dragStartTime = 0;
        this.wasDragging = false;
        
        // These will be calculated dynamically in init()
        this.visibleItemsOffset = 0;
        this.visibleItemsOffsetRight = 0;

        // Direction detection properties
        this.dragDirection = null;
        this.dragDirectionThreshold = 5;
        this.isDirectionSet = false;
        
        // Hover/active properties
        this.isHovered = false;
        this.lastHoverTime = 0;
        
        // Momentum properties
        this.isMomentumAnimating = false;
        this.velocity = 0;
        this.friction = 0.92;
        this.minVelocity = 0.5;
        
        // Auto-animation properties
        this.isAutoAnimating = false;
        this.autoDirection = 1;
        this.autoSpeed = options.autoSpeed || 50;
        this.autoVelocity = 0;
        this.shouldResumeAuto = false;
        
        // Auto-slideshow properties
        this.isSlideshow = false;
        this.slideshowInterval = options.slideshowInterval || 6000;
        this.slideshowTimer = null;
        this.slideshowDirection = 1;
        this.shouldResumeSlideshow = false;
        this.slideshowStartTime = 0;
        this.slideshowPausedTime = 0;
        this.slideshowElapsedBeforePause = 0;
        
        // Progress bar properties
        this.progressBar = container.querySelector('.arcanov-slider__progress');
        this.progressFill = this.progressBar ? this.progressBar.querySelector('.arcanov-slider__progress-fill') : null;
        this.progressConfig = config.autoSlideshow?.progressBar || { enabled: false, type: 'linear' };
        this.progressAnimationFrame = null;
        
        // Velocity tracking
        this.lastPositions = [];
        this.maxHistoryLength = 5;
        
        // Wheel scroll properties
        this.isWheelScrolling = false;
        this.wheelAccumulator = 0;
        this.wheelThreshold = 50;
        this.wheelTimeout = null;
        this.wheelDebounceTime = 150;

        // Resize debouncing properties
        this.resizeTimeout = null;
        this.resizeDebounceTime = 500;
        this.isResizing = false;
        
        // Wrap around animation properties
        this.wrapAroundDuration = 500; // Duration for wrap around animation
        this.wrapAroundEasing = 'ease-out'; // CSS easing for wrap around
        
        this.options = {
            infinite: config.isInfiniteLoop !== false,
            steps: options.steps || false,
            animating: options.animating || false,
            startPosition: options.startPosition || null,
            direction: options.direction || 'horizontal',
            enableWheelScroll: config.enableWheelScroll !== false,
            readingDirection: config.readingDirection || 'ltr' // 'ltr' or 'rtl'
        };

        this.maxOffset = 0; // START
        this.minOffset = 0; // END

        this.init();
        this.setupEventListeners();
        this.setupClickBlocking(); // Added click blocking
        this.setupNavigationListeners();
        this.setupKeyboardListeners();
        this.setupWheelListeners();
        window.addEventListener('resize', () => this.handleResizeDebounced());
        
        // Add orientation change listener for mobile
        window.addEventListener('orientationchange', () => {
            // Delay to allow for orientation change to complete
            setTimeout(() => {
                this.handleResizeDebounced();
            }, 300);
        });
    }

    trueModulo(value, mod) {
        return ((value % mod) + mod) % mod;
    }

    calculateItemWidth() {
        if (this.originalItems.length === 0) return 200;
        
        // Pobierz faktyczny wymiar pierwszego elementu (uwzględnia CSS max-width)
        const firstElement = this.originalItems[0];
        const computedStyle = window.getComputedStyle(firstElement);
        const actualWidth = firstElement.getBoundingClientRect().width;
        
        if (actualWidth > 0) {
            return actualWidth;
        }
        
        // Fallback - sprawdź style config
        if (this.styleConfig.width) {
            if (this.styleConfig.width.includes('px')) {
                const pxWidth = parseInt(this.styleConfig.width);
                
                // Sprawdź czy max-width ogranicza szerokość (tylko jeśli jest ustawione)
                if (this.styleConfig.maxWidth && this.styleConfig.maxWidth.includes('vw')) {
                    const vw = parseFloat(this.styleConfig.maxWidth);
                    const maxWidthPx = (window.innerWidth * vw) / 100;
                    return Math.min(pxWidth, maxWidthPx);
                }
                
                return pxWidth;
            } else if (this.styleConfig.width.includes('vw')) {
                const vw = parseFloat(this.styleConfig.width);
                let calculatedWidth = (window.innerWidth * vw) / 100;
                
                // Sprawdź max-width (tylko jeśli jest ustawione)
                if (this.styleConfig.maxWidth && this.styleConfig.maxWidth.includes('vw')) {
                    const maxVw = parseFloat(this.styleConfig.maxWidth);
                    const maxWidthPx = (window.innerWidth * maxVw) / 100;
                    calculatedWidth = Math.min(calculatedWidth, maxWidthPx);
                }
                
                return calculatedWidth;
            } else if (this.styleConfig.width.includes('%')) {
                const percent = parseFloat(this.styleConfig.width);
                return (this.container.offsetWidth * percent) / 100;
            }
        }
        
        return firstElement.offsetWidth || 200;
    }

    recalculateItemDimensions() {
        const oldItemWidth = this.itemWidth;
        this.itemWidth = this.calculateItemWidth();
        
        // Jeśli wymiar się zmienił, zaktualizuj pozycje proporcjonalnie
        if (Math.abs(oldItemWidth - this.itemWidth) > 1) {
            const scaleFactor = this.itemWidth / oldItemWidth;
            
            this.currentOffset *= scaleFactor;
            this.baseOffset *= scaleFactor;
            
            if (!this.options.infinite) {
                const totalWidth = this.originalItems.length * this.itemWidth;
                this.maxOffset = 0;
                this.minOffset = this.containerWidth - totalWidth;
                this.currentOffset = this.clampOffset(this.currentOffset);
                this.baseOffset = this.currentOffset;
            }
            
            this.updateTransform();
            return true; // Wymiar się zmienił
        }
        
        return false; // Wymiar bez zmian
    }

    init() {
        this.containerWidth = this.container.offsetWidth;
        
        if (this.options.infinite) {
            // Calculate visible elements count based on container and item dimensions
            const visibleElementsCount = Math.ceil(this.containerWidth / this.itemWidth);
            
            // Calculate buffer size - should be generous enough to handle smooth scrolling
            // Use at least double the visible elements, but minimum 4 elements buffer
            const minBuffer = 4;
            const calculatedBuffer = Math.max(minBuffer, visibleElementsCount * 2);
            
            this.visibleItemsOffset = calculatedBuffer;
            this.visibleItemsOffsetRight = calculatedBuffer;
            
            // Total items to render = visible + buffer
            this.visibleItems = visibleElementsCount + this.visibleItemsOffset;
            
            // Populate track with calculated values
            this.populateTrack();
            
            // Calculate baseOffset to show first element (index 0) at the start
            // We need to offset by the left buffer amount
            const leftBufferElements = Math.floor(this.visibleItemsOffset / 2);
            this.baseOffset = -this.itemWidth * leftBufferElements;
            this.currentOffset = this.baseOffset;
        } else {
            this.visibleItems = this.originalItems.length;
            const totalWidth = this.originalItems.length * this.itemWidth;
            this.maxOffset = 0; // START position
            this.minOffset = this.containerWidth - totalWidth; // END position
            
            this.baseOffset = 0;
            this.currentOffset = 0;
        }
        
        if (this.options.startPosition && this.options.startPosition.x) {
            this.currentOffset += this.options.startPosition.x;
        }
        
        if (this.options.animating && this.options.animating.x) {
            const speed = Math.abs(this.options.animating.x) * 100;
            const direction = this.options.animating.x > 0 ? 1 : -1;
            this.startAutoAnimation(direction, speed);
        }
        
        this.updateTransform();
    }

    // Click blocking implementation
    setupClickBlocking() {
        // Universal click blocking for any interactive element
        this.container.addEventListener('click', (e) => {
            if (this.wasDragging) {
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();
                return false;
            }
        }, true); // capture phase - intercepts BEFORE other handlers

        // Additional mousedown handling for better UX
        this.container.addEventListener('mousedown', (e) => {
            // Prevent focus on links during potential drag
            if (e.target.blur) {
                e.target.blur();
            }
        }, true);
    }

    // Progress Bar Methods
    updateProgressBar(progress) {
        if (!this.progressFill) return;
        
        // Clamp progress between 0 and 1
        progress = Math.max(0, Math.min(1, progress));
        
        if (this.progressConfig.type === 'circular') {
            const circumference = 62.83; // 2 * PI * 10
            const offset = circumference - (progress * circumference);
            this.progressFill.style.strokeDashoffset = offset;
        } else {
            this.progressFill.style.width = `${progress * 100}%`;
        }
    }

    updateProgressFromSlideshowState() {
        if (!this.progressBar || !this.progressConfig.enabled || !this.isSlideshow) {
            return;
        }
        
        let progress = 0;
        
        if (this.slideshowPausedTime > 0) {
            // Currently paused - show progress up to pause point
            progress = this.slideshowElapsedBeforePause / this.slideshowInterval;
        } else {
            // Currently running - calculate current progress
            const now = Date.now();
            const totalElapsed = this.slideshowElapsedBeforePause + (now - this.slideshowStartTime);
            progress = totalElapsed / this.slideshowInterval;
        }
        
        this.updateProgressBar(progress);
        
        // Continue animation if slideshow is running
        if (this.isSlideshow && this.slideshowPausedTime === 0) {
            this.progressAnimationFrame = requestAnimationFrame(() => this.updateProgressFromSlideshowState());
        }
    }

    // Check if slider is at boundaries (for non-infinite mode)
    isAtStart() {
        if (this.options.infinite) return false;
        return this.currentOffset >= this.maxOffset - 10; // 10px tolerance
    }

    isAtEnd() {
        if (this.options.infinite) return false;
        return this.currentOffset <= this.minOffset + 10; // 10px tolerance
    }

    // Jump to opposite end for wrap-around with smooth animation
    jumpToStart() {
        if (this.options.infinite) return;
        
        this.track.style.transition = `transform ${this.wrapAroundDuration}ms ${this.wrapAroundEasing}`;
        this.currentOffset = this.maxOffset;
        this.baseOffset = this.currentOffset;
        this.updateTransform();
        
        setTimeout(() => {
            this.track.style.transition = 'none';
        }, this.wrapAroundDuration);
    }

    jumpToEnd() {
        if (this.options.infinite) return;
        
        this.track.style.transition = `transform ${this.wrapAroundDuration}ms ${this.wrapAroundEasing}`;
        this.currentOffset = this.minOffset;
        this.baseOffset = this.currentOffset;
        this.updateTransform();
        
        setTimeout(() => {
            this.track.style.transition = 'none';
        }, this.wrapAroundDuration);
    }

    // Execute movement with reading-direction-aware wrap around for non-infinite mode
    executeMovement(direction, allowWrapAround = false, fromSlideshow = false) {
        // Check if we need to wrap around in non-infinite mode based on reading direction
        let shouldWrapAround = false;
        
        if (!this.options.infinite && allowWrapAround) {
            // Reading direction determines wrap around behavior
            if (this.options.readingDirection === 'ltr') {
                // LTR: Only wrap from end to start (like turning page in book)
                if (direction > 0 && this.isAtEnd()) {
                    shouldWrapAround = true;
                }
            } else if (this.options.readingDirection === 'rtl') {
                // RTL: Only wrap from start to end (Arabic/Hebrew reading pattern)
                if (direction < 0 && this.isAtStart()) {
                    shouldWrapAround = true;
                }
            }
        }
        
        if (shouldWrapAround) {
            // For slideshow wrap around, don't stop slideshow
            if (!fromSlideshow && this.isSlideshow) {
                this.stopSlideshow();
                this.shouldResumeSlideshow = true;
            }
            
            // Jump to opposite end based on reading direction
            if (this.options.readingDirection === 'ltr') {
                this.jumpToStart(); // End reached, go to start
            } else {
                this.jumpToEnd(); // Start reached, go to end
            }
            
            // If not from slideshow, restart slideshow after wrap around (wait for animation)
            if (!fromSlideshow && this.shouldResumeSlideshow) {
                setTimeout(() => {
                    if (this.shouldResumeSlideshow && !this.isSlideshow) {
                        this.startSlideshow(this.slideshowDirection, this.slideshowInterval);
                    }
                }, this.wrapAroundDuration + 100); // Wait for wrap around animation to complete + buffer
            }
            
            return true; // Indicate that wrap around occurred
        } else {
            // Normal movement
            if (direction > 0) {
                this.moveRightSmooth(fromSlideshow);
            } else {
                this.moveLeftSmooth(fromSlideshow);
            }
            return false; // Normal movement occurred
        }
    }

    populateTrack(targetSlideIndex = 0) {
        this.track.innerHTML = '';
        
        const leftBufferElements = Math.floor(this.visibleItemsOffset / 2);
        const startIndex = this.trueModulo(targetSlideIndex - leftBufferElements, this.originalItems.length);
        
        for (let i = 0; i < this.visibleItems; i++) {
            const originalIndex = this.trueModulo(startIndex + i, this.originalItems.length);
            const clonedElement = this.originalItems[originalIndex].cloneNode(true);
            clonedElement.setAttribute('data-sequence', originalIndex);
            this.track.appendChild(clonedElement);
        }
    }

    clampOffset(offset) {
        if (this.options.infinite) return offset;
        return Math.max(this.minOffset, Math.min(this.maxOffset, offset));
    }

    addItemAtPosition(itemIndex, direction) {
        if (!this.options.infinite) return;

        const safeItemIndex = this.trueModulo(itemIndex, this.originalItems.length);
        
        const originalElement = this.originalItems[safeItemIndex];
        const clonedElement = originalElement.cloneNode(true);
        clonedElement.setAttribute('data-sequence', safeItemIndex);

        if (direction === 'right') {
            this.track.appendChild(clonedElement);
        } else {
            this.track.insertBefore(clonedElement, this.track.firstChild);
        }
    }

    removeItem(direction) {
        if (!this.options.infinite) return;
        
        if (direction === 'right' && this.track.lastElementChild) {
            this.track.removeChild(this.track.lastElementChild);
        } else if (direction === 'left' && this.track.firstElementChild) {
            this.track.removeChild(this.track.firstElementChild);
        }
    }

    setupWheelListeners() {
        if (!this.options.enableWheelScroll) return;
        
        this.container.addEventListener('wheel', (e) => {
            this.handleWheelScroll(e);
        }, { passive: false });
    }

    handleWheelScroll(e) {
        const deltaX = e.deltaX;
        const deltaY = e.deltaY;
        
        const isHorizontalScroll = Math.abs(deltaX) > Math.abs(deltaY);
        const isSliderHorizontal = this.options.direction === 'horizontal';
        
        if (!isHorizontalScroll || !isSliderHorizontal) {
            return;
        }
        
        if (this.isSlideshow) {
            this.pauseSlideshow();
        }
        if (this.isAutoAnimating) {
            this.pauseAutoAnimation();
        }
        
        e.preventDefault();
        
        this.wheelAccumulator += deltaX;
        this.isWheelScrolling = true;
        
        const sensitivity = 0.8;
        const movement = deltaX * sensitivity;
        
        const newOffset = this.currentOffset - movement;
        this.currentOffset = this.clampOffset(newOffset);
        
        this.checkAndAddElements();
        this.updateTransform();
        
        if (this.wheelTimeout) {
            clearTimeout(this.wheelTimeout);
        }
        
        this.wheelTimeout = setTimeout(() => {
            this.endWheelScroll();
        }, this.wheelDebounceTime);
    }

    endWheelScroll() {
        if (!this.isWheelScrolling) return;
        
        this.isWheelScrolling = false;
        
        const accumulatedMovement = Math.abs(this.wheelAccumulator);
        
        if (accumulatedMovement > this.wheelThreshold) {
            const direction = this.wheelAccumulator > 0 ? -1 : 1;
            const velocity = Math.min(accumulatedMovement / 100, 8) * direction;
            
            this.startMomentum(velocity);
        } else {
            this.snapToPosition();
        }
        
        this.wheelAccumulator = 0;
        
        // Don't resume slideshow here - it will be handled by finalizePosition/snapToPosition
        setTimeout(() => {
            this.resumeAutoAnimation();
        }, 100);
    }

    setupNavigationListeners() {
        const navElements = document.querySelectorAll(`[for-slider="${this.sliderId}"]`);
        
        navElements.forEach(navElement => {
            navElement.addEventListener('click', (e) => {
                e.preventDefault();
                
                if (navElement.classList.contains('arcanov-slider__navigation--next')) {
                    this.nextSlideWithWrapAround();
                } else if (navElement.classList.contains('arcanov-slider__navigation--prev')) {
                    this.prevSlideWithWrapAround();
                }
            });
            
            navElement.style.cursor = 'pointer';
        });
    }

    setupKeyboardListeners() {
        this.container.addEventListener('mouseenter', () => {
            this.setHovered(true);
        });
        
        this.container.addEventListener('mouseleave', () => {
            this.setHovered(false);
        });
        
        if (!window.arcanovKeyboardListenerAdded) {
            window.arcanovKeyboardListenerAdded = true;
            
            document.addEventListener('keydown', (e) => {
                const hoveredSlider = this.getHoveredSlider();
                if (!hoveredSlider) return;
                
                if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) {
                    return;
                }
                
                switch(e.key) {
                    case 'ArrowLeft':
                        e.preventDefault();
                        hoveredSlider.prevSlideWithWrapAround();
                        break;
                    case 'ArrowRight':
                        e.preventDefault();
                        hoveredSlider.nextSlideWithWrapAround();
                        break;
                    case 'ArrowUp':
                        if (hoveredSlider.options.direction === 'vertical') {
                            e.preventDefault();
                            hoveredSlider.prevSlideWithWrapAround();
                        }
                        break;
                    case 'ArrowDown':
                        if (hoveredSlider.options.direction === 'vertical') {
                            e.preventDefault();
                            hoveredSlider.nextSlideWithWrapAround();
                        }
                        break;
                }
            });
        }
    }

    setHovered(isHovered) {
        this.isHovered = isHovered;
        this.lastHoverTime = Date.now();
        
        if (isHovered) {
            if (window.arcanovSliders) {
                Object.values(window.arcanovSliders).forEach(slider => {
                    if (slider !== this) {
                        slider.isHovered = false;
                        slider.container.classList.remove('arcanov-slider--hovered');
                    }
                });
            }
            
            this.container.classList.add('arcanov-slider--hovered');
        } else {
            this.container.classList.remove('arcanov-slider--hovered');
        }
    }

    getHoveredSlider() {
        if (!window.arcanovSliders) return null;
        return Object.values(window.arcanovSliders).find(slider => slider.isHovered) || null;
    }

    determineDragDirection(deltaX, deltaY) {
        const absX = Math.abs(deltaX);
        const absY = Math.abs(deltaY);
        
        if (absX < this.dragDirectionThreshold && absY < this.dragDirectionThreshold) {
            return null;
        }
        
        if (absX > absY) {
            return 'horizontal';
        } else {
            return 'vertical';
        }
    }

    isValidDragDirection(dragDirection) {
        if (!dragDirection) return false;
        
        if (this.options.direction === 'horizontal') {
            return dragDirection === 'horizontal';
        }
        
        if (this.options.direction === 'vertical') {
            return dragDirection === 'vertical';
        }
        
        return true;
    }

    moveLeftSmooth(fromSlideshow = false) {
        if (this.isSlideshow && !fromSlideshow) {
            // Manual slide change - reset slideshow cycle completely
            this.stopSlideshow();
            this.shouldResumeSlideshow = true; // Allow it to restart later
        }
        
        this.stopAutoAnimation();
        this.stopMomentum();
        
        const requiredVelocity = 0.8;
        this.startMomentumFromButton(requiredVelocity);
    }

    moveRightSmooth(fromSlideshow = false) {
        if (this.isSlideshow && !fromSlideshow) {
            // Manual slide change - reset slideshow cycle completely
            this.stopSlideshow();
            this.shouldResumeSlideshow = true; // Allow it to restart later
        }
        
        this.stopAutoAnimation();
        this.stopMomentum();
        
        const requiredVelocity = -0.8;
        this.startMomentumFromButton(requiredVelocity);
    }

    // Button/Keyboard methods with wrap around support
    prevSlideWithWrapAround() {
        this.stopAutoAnimation();
        this.stopMomentum();
        this.executeMovement(-1, true, false);
    }

    nextSlideWithWrapAround() {
        this.stopAutoAnimation();
        this.stopMomentum();
        this.executeMovement(1, true, false);
    }

    startMomentumFromButton(velocity) {
        this.startMomentum(velocity);
    }

    startSlideshow(direction = 1, interval = this.slideshowInterval) {
        this.stopAllAnimations();
        
        this.isSlideshow = true;
        this.slideshowDirection = direction;
        this.slideshowInterval = interval;
        this.shouldResumeSlideshow = true;
        
        this.startSlideshowCycle();
    }

    stopSlideshow() {
        this.isSlideshow = false;
        this.shouldResumeSlideshow = false;
        
        if (this.slideshowTimer) {
            clearTimeout(this.slideshowTimer);
            this.slideshowTimer = null;
        }
        
        this.slideshowStartTime = 0;
        this.slideshowPausedTime = 0;
        this.slideshowElapsedBeforePause = 0;
        this.container.classList.remove('paused');
        
        if (this.progressAnimationFrame) {
            cancelAnimationFrame(this.progressAnimationFrame);
        }
        
        this.updateProgressBar(0);
    }

    pauseSlideshow() {
        if (!this.isSlideshow) return;
        
        // Pause the main slideshow timer
        this.isSlideshow = false;
        this.shouldResumeSlideshow = true;
        this.slideshowPausedTime = Date.now();
        
        // Calculate elapsed time before pause
        if (this.slideshowStartTime > 0) {
            this.slideshowElapsedBeforePause += this.slideshowPausedTime - this.slideshowStartTime;
        }
        
        // Clear the timer
        if (this.slideshowTimer) {
            clearTimeout(this.slideshowTimer);
            this.slideshowTimer = null;
        }
        
        this.container.classList.add('paused');
        
        // Stop progress animation
        if (this.progressAnimationFrame) {
            cancelAnimationFrame(this.progressAnimationFrame);
        }
    }

    resumeSlideshow() {
        if (!this.shouldResumeSlideshow || this.isSlideshow) return;
        
        this.isSlideshow = true;
        this.slideshowStartTime = Date.now();
        this.slideshowPausedTime = 0;
        
        // Calculate remaining time
        const remainingTime = this.slideshowInterval - this.slideshowElapsedBeforePause;
        
        this.container.classList.remove('paused');
        
        // Resume progress animation
        this.updateProgressFromSlideshowState();
        
        // Set timer for remaining time
        this.slideshowTimer = setTimeout(() => {
            if (!this.isSlideshow) return;
            
            // Use executeMovement with wrap around enabled for slideshow
            const wasWrapAround = this.executeMovement(this.slideshowDirection, true, true);
            
            // Different timing based on whether wrap around occurred
            const nextCycleDelay = wasWrapAround ? this.wrapAroundDuration + 100 : 300;
            
            setTimeout(() => {
                if (this.isSlideshow) {
                    this.startSlideshowCycle();
                }
            }, nextCycleDelay);
            
        }, Math.max(0, remainingTime));
    }

    startSlideshowCycle() {
        if (!this.isSlideshow) return;
        
        // Reset slideshow timing
        this.slideshowStartTime = Date.now();
        this.slideshowElapsedBeforePause = 0;
        this.slideshowPausedTime = 0;
        
        // Start progress animation
        this.updateProgressBar(0);
        this.updateProgressFromSlideshowState();
        
        this.slideshowTimer = setTimeout(() => {
            if (!this.isSlideshow) return;
            
            // Use executeMovement with wrap around enabled for slideshow
            const wasWrapAround = this.executeMovement(this.slideshowDirection, true, true);
            
            // Different timing based on whether wrap around occurred
            const nextCycleDelay = wasWrapAround ? this.wrapAroundDuration + 100 : 300;
            
            setTimeout(() => {
                if (this.isSlideshow) {
                    this.startSlideshowCycle();
                }
            }, nextCycleDelay);
            
        }, this.slideshowInterval);
    }

    setSlideshowInterval(interval) {
        this.slideshowInterval = interval;
        
        if (this.isSlideshow) {
            if (this.slideshowTimer) {
                clearTimeout(this.slideshowTimer);
            }
            this.startSlideshowCycle();
        }
    }

    startAutoAnimation(direction = 1, speed = 50) {
        this.stopAllAnimations();
        
        this.autoDirection = direction;
        this.autoSpeed = speed;
        this.autoVelocity = (speed / 60) * direction;
        this.isAutoAnimating = true;
        this.shouldResumeAuto = true;
        
        this.track.style.transition = 'none';
        this.animateAuto();
    }

    stopAutoAnimation() {
        this.isAutoAnimating = false;
        this.shouldResumeAuto = false;
    }

    pauseAutoAnimation() {
        this.isAutoAnimating = false;
    }

    resumeAutoAnimation() {
        if (this.shouldResumeAuto && !this.isAutoAnimating) {
            this.isAutoAnimating = true;
            this.track.style.transition = 'none';
            this.animateAuto();
        }
    }

    setAutoSpeed(speed) {
        this.autoSpeed = speed;
        this.autoVelocity = (speed / 60) * this.autoDirection;
    }

    animateAuto() {
        if (!this.isAutoAnimating) return;
        
        const newOffset = this.currentOffset + this.autoVelocity;
        this.currentOffset = this.clampOffset(newOffset);
        
        this.checkAndAddElements();
        this.updateTransform();
        
        requestAnimationFrame(() => this.animateAuto());
    }

    stopAllAnimations() {
        this.stopMomentum();
        this.stopAutoAnimation();
        
        if (this.wheelTimeout) {
            clearTimeout(this.wheelTimeout);
            this.wheelTimeout = null;
        }
        this.isWheelScrolling = false;
        this.wheelAccumulator = 0;
    }

    updateTransform() {
        this.track.style.transform = `translateX(${this.currentOffset}px)`;
    }

    handleResizeDebounced() {
        // IMMEDIATE corrections (throttled)
        this.handleResizeImmediate();
        
        // // HEAVY operations (debounced)
        // this.isResizing = true;
        
        // if (this.resizeTimeout) {
        //     clearTimeout(this.resizeTimeout);
        // }
        
        // this.resizeTimeout = setTimeout(() => {
        //     this.handleResizeHeavy();
        //     this.isResizing = false;
        // }, this.resizeDebounceTime);
    }

    handleResizeImmediate() {
        const newWidth = this.container.offsetWidth;
        const previousWidth = this.containerWidth;
        
        // Skip if size didn't change or invalid
        if (newWidth === previousWidth || newWidth <= 0) {
            return;
        }
        
        // === RECALCULATE ITEM DIMENSIONS ===
        const dimensionsChanged = this.recalculateItemDimensions();
        
        // === TYLKO SZYBKIE KOREKTY POZYCJI ===
        // Zapisz pozycję kotwicy PRZED zmianą
        let absolutePositionFromStart = 0;
        let anchorSequence = 0;
        
        if (this.options.infinite && this.track.children.length > 0) {
            const anchorInfo = this.findBestAnchorElement();
            if (anchorInfo) {
                anchorSequence = anchorInfo.sequence;
                const anchorPositionInTrack = anchorInfo.index * this.itemWidth;
                const anchorPositionInContainer = this.currentOffset + anchorPositionInTrack;
                absolutePositionFromStart = -anchorPositionInContainer;
            } else {
                absolutePositionFromStart = -this.currentOffset;
            }
        } else {
            absolutePositionFromStart = -this.currentOffset;
        }
        
        // Update wymiary
        this.containerWidth = newWidth;
        
        // Jeśli wymiary slajdów się zmieniły, już zostały zaktualizowane w recalculateItemDimensions()
        if (!dimensionsChanged) {
            const oldItemWidth = this.itemWidth;
            this.itemWidth = this.calculateItemWidth();
            
            // Przeskaluj pozycję tylko jeśli itemWidth się zmienił
            if (Math.abs(oldItemWidth - this.itemWidth) > 1) {
                const scaledAbsolutePosition = (absolutePositionFromStart / oldItemWidth) * this.itemWidth;
                
                if (this.options.infinite) {
                    // Znajdź kotwicę po resize i przywróć pozycję
                    let anchorIndex = -1;
                    for (let i = 0; i < this.track.children.length; i++) {
                        const sequence = parseInt(this.track.children[i].getAttribute('data-sequence') || -1);
                        if (sequence === anchorSequence) {
                            anchorIndex = i;
                            break;
                        }
                    }
                    
                    if (anchorIndex >= 0) {
                        const anchorPositionInTrack = anchorIndex * this.itemWidth;
                        const desiredAnchorPositionInContainer = -scaledAbsolutePosition;
                        this.currentOffset = desiredAnchorPositionInContainer - anchorPositionInTrack;
                    } else {
                        this.currentOffset = scaledAbsolutePosition;
                    }
                } else {
                    // Non-infinite
                    const totalWidth = this.originalItems.length * this.itemWidth;
                    this.maxOffset = 0;
                    this.minOffset = this.containerWidth - totalWidth;
                    this.currentOffset = this.clampOffset(scaledAbsolutePosition);
                    this.baseOffset = this.currentOffset;
                }
            }
        }
        
        // Tylko update transform - BEZ populateTrack()!
        this.updateTransform();
    }

    handleResizeHeavy() {
        // === CIĘŻKIE OPERACJE - KLONOWANIE ===
        const wasSlideshow = this.isSlideshow;
        const slideshowDirection = this.slideshowDirection;
        const slideshowInterval = this.slideshowInterval;
        const wasAnimating = this.isAutoAnimating;
        
        this.stopAllAnimations();
        this.stopSlideshow();
        
        if (this.options.infinite) {
            // Update visible items count
            const newVisibleItems = Math.ceil(this.containerWidth / this.itemWidth) + this.visibleItemsOffset;
            
            // KLONOWANIE tylko gdy rzeczywiście trzeba
            const itemCountDifference = Math.abs(newVisibleItems - this.visibleItems);
            if (itemCountDifference > 2) {
                // console.log(`🔥 Heavy resize: repopulating track (${this.visibleItems} → ${newVisibleItems})`);
                
                // Zapisz pozycję przed populateTrack
                const anchorInfo = this.findBestAnchorElement();
                
                this.visibleItems = newVisibleItems;
                this.populateTrack(); // ← KLONOWANIE tutaj
                
                // Przywróć pozycję po populateTrack
                if (anchorInfo) {
                    let anchorIndex = -1;
                    for (let i = 0; i < this.track.children.length; i++) {
                        const sequence = parseInt(this.track.children[i].getAttribute('data-sequence') || -1);
                        if (sequence === anchorInfo.sequence) {
                            anchorIndex = i;
                            break;
                        }
                    }
                    
                    if (anchorIndex >= 0) {
                        // Zachowaj pozycję kotwicy ustawioną w immediate
                        // (immediate już zaktualizował currentOffset)
                    }
                }
                
                this.updateTransform();
            } else {
                this.visibleItems = newVisibleItems;
            }
            
            // Update baseOffset
            this.baseOffset = -this.itemWidth * Math.floor(this.visibleItemsOffset / 2);
        }
        
        // Resume animations
        setTimeout(() => {
            if (wasSlideshow) {
                this.startSlideshow(slideshowDirection, slideshowInterval);
            }
            if (wasAnimating && this.shouldResumeAuto) {
                this.resumeAutoAnimation();
            }
        }, 100);
    }

    // Helper function to find the best anchor element (most visible slide)
    findBestAnchorElement() {
        if (!this.track.children.length) return null;
        
        // For infinite mode, try to find sequence="0" first as a stable reference
        if (this.options.infinite) {
            for (let i = 0; i < this.track.children.length; i++) {
                const sequence = parseInt(this.track.children[i].getAttribute('data-sequence') || -1);
                if (sequence === 0) {
                    return { element: this.track.children[i], sequence: 0, index: i };
                }
            }
        }
        
        // If sequence="0" not found, find the element closest to the left edge (most visible)
        let bestElement = null;
        let bestDistance = Infinity;
        let bestIndex = 0;
        let bestSequence = 0;
        
        for (let i = 0; i < this.track.children.length; i++) {
            const element = this.track.children[i];
            const elementPositionInTrack = i * this.itemWidth;
            const elementPositionInContainer = this.currentOffset + elementPositionInTrack;
            
            // Distance from left edge of container
            const distanceFromLeft = Math.abs(elementPositionInContainer);
            
            if (distanceFromLeft < bestDistance) {
                bestDistance = distanceFromLeft;
                bestElement = element;
                bestIndex = i;
                bestSequence = parseInt(element.getAttribute('data-sequence') || 0);
            }
        }
        
        return bestElement ? { element: bestElement, sequence: bestSequence, index: bestIndex } : null;
    }

    addPositionToHistory(x, timestamp) {
        this.lastPositions.push({ x, timestamp });
        
        const cutoff = timestamp - 100;
        this.lastPositions = this.lastPositions.filter(pos => pos.timestamp > cutoff);
        
        if (this.lastPositions.length > this.maxHistoryLength) {
            this.lastPositions = this.lastPositions.slice(-this.maxHistoryLength);
        }
    }

    calculateVelocity() {
        if (this.lastPositions.length < 2) return 0;
        
        const oldest = this.lastPositions[0];
        const newest = this.lastPositions[this.lastPositions.length - 1];
        
        const deltaX = newest.x - oldest.x;
        const deltaTime = newest.timestamp - oldest.timestamp;
        
        if (deltaTime === 0) return 0;
        return deltaX / deltaTime;
    }

    setupEventListeners() {
        this.track.addEventListener('mousedown', (e) => {
            if (this.isSlideshow) {
                this.pauseSlideshow();
            }
            this.startDrag(e);
        });
        
        document.addEventListener('mousemove', (e) => this.drag(e));
        document.addEventListener('mouseup', () => {
            this.endDrag();
        });
        
        this.track.addEventListener('dragstart', (e) => e.preventDefault());

        this.track.addEventListener('touchstart', (e) => {
            if (this.isMomentumAnimating || this.isAutoAnimating) {
                if (this.isAutoAnimating) {
                    this.pauseAutoAnimation();
                }
                this.stopMomentum();
            }
            
            if (this.isSlideshow) {
                this.pauseSlideshow();
            }
            
            this.startDrag(e.touches[0]);
        }, { passive: false });

        this.track.addEventListener('touchmove', (e) => {
            if (this.isDragging) {
                const shouldHandleDrag = this.drag(e.touches[0]);
                
                if (shouldHandleDrag) {
                    e.preventDefault();
                }
            }
        }, { passive: false });

        this.track.addEventListener('touchend', (e) => {
            this.endDrag();
        }, { passive: false });
    }

    startDrag(e) {
        if (this.isAutoAnimating) {
            this.pauseAutoAnimation();
        }
        this.stopMomentum();
        
        this.isDragging = true;
        this.startX = e.clientX;
        this.startY = e.clientY;
        this.startOffset = this.currentOffset;
        this.track.style.cursor = 'grabbing';
        
        // Initialize click blocking properties
        this.dragDistance = 0;
        this.dragStartTime = Date.now();
        this.wasDragging = false;
        
        this.dragDirection = null;
        this.isDirectionSet = false;
        
        this.lastPositions = [];
        const now = Date.now();
        this.addPositionToHistory(e.clientX, now);
    }

    drag(e) {
        if (!this.isDragging) return false;
        
        const deltaX = e.clientX - this.startX;
        const deltaY = e.clientY - this.startY;
        
        // Update drag distance for click blocking
        this.dragDistance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
        
        if (!this.isDirectionSet) {
            const detectedDirection = this.determineDragDirection(deltaX, deltaY);
            
            if (detectedDirection) {
                this.dragDirection = detectedDirection;
                this.isDirectionSet = true;
                
                if (!this.isValidDragDirection(this.dragDirection)) {
                    return false;
                }
            } else {
                return false;
            }
        }
        
        if (!this.isValidDragDirection(this.dragDirection)) {
            return false;
        }
        
        const delta = this.options.direction === 'horizontal' ? deltaX : deltaY;
        const newOffset = this.startOffset + delta;
        this.currentOffset = this.clampOffset(newOffset);
        
        const now = Date.now();
        this.addPositionToHistory(e.clientX, now);
        
        this.checkAndAddElements();
        this.updateTransform();
        
        return true;
    }

    checkAndAddElements() {
        if (!this.options.infinite) return;
        
        const leftBufferElements = Math.floor(this.visibleItemsOffset / 2);
        const leftThreshold = -this.itemWidth * (leftBufferElements - 2);
        
        if (this.currentOffset > leftThreshold) {
            const firstItemIndex = parseInt(this.track.firstElementChild.getAttribute('data-sequence'));
            
            const prevItemIndex = this.trueModulo(firstItemIndex - 1, this.originalItems.length);
            
            this.addItemAtPosition(prevItemIndex, 'left');
            
            this.currentOffset -= this.itemWidth;
            this.startOffset -= this.itemWidth;
            this.baseOffset -= this.itemWidth;
            
            if (this.track.children.length > this.visibleItems) {
                this.removeItem('right');
            }
        }
        
        const rightThreshold = -this.itemWidth * (this.visibleItems - this.visibleItemsOffsetRight);
        if (this.currentOffset < rightThreshold) {
            const lastItemIndex = parseInt(this.track.lastElementChild.getAttribute('data-sequence'));
            
            const nextItemIndex = this.trueModulo(lastItemIndex + 1, this.originalItems.length);
            
            this.addItemAtPosition(nextItemIndex, 'right');
            
            if (this.track.children.length > this.visibleItems) {
                this.removeItem('left');
                this.currentOffset += this.itemWidth;
                this.startOffset += this.itemWidth;
                this.baseOffset += this.itemWidth;
            }
        }
    }

    endDrag() {
        if (!this.isDragging) return;
        this.isDragging = false;
        this.track.style.cursor = 'grab';
        
        const dragTime = Date.now() - this.dragStartTime;
        
        // Determine if there was a significant drag for click blocking
        this.wasDragging = this.dragDistance > 3 || dragTime > 150;
        
        this.dragDirection = null;
        this.isDirectionSet = false;
        
        const velocity = this.calculateVelocity();
        
        // If there was significant movement, it will trigger momentum/snap which handles slideshow
        // If no significant movement, resume slideshow immediately
        if (Math.abs(velocity) < 0.1) {
            setTimeout(() => {
                if (this.shouldResumeSlideshow && !this.isSlideshow) {
                    this.resumeSlideshow();
                }
            }, 100);
        }
        
        this.startMomentum(velocity);
        
        // Reset drag blocking flag after a short delay
        setTimeout(() => {
            this.wasDragging = false;
        }, 50);
    }

    startMomentum(initialVelocity) {
        if (isNaN(initialVelocity) || !isFinite(initialVelocity)) {
            this.snapToPosition();
            return;
        }
        
        const velocityStrength = Math.abs(initialVelocity);
        const direction = initialVelocity > 0 ? 1 : -1;

        let elementsToMove = 0;
        if (velocityStrength > 14) {
            elementsToMove = 4;
        } else if (velocityStrength > 10) {
            elementsToMove = 3;
        } else if (velocityStrength > 6) {
            elementsToMove = 2;
        } else if (velocityStrength > 0.2) {
            elementsToMove = 1;
        }
        
        // Calculate targetOffset same as for infinite mode
        const currentElementOffset = this.currentOffset - this.baseOffset;
        const currentElementsMoved = Math.round(currentElementOffset / this.itemWidth);
        
        let targetElementsMoved;
        if (elementsToMove === 0) {
            targetElementsMoved = currentElementsMoved;
        } else {
            targetElementsMoved = currentElementsMoved + (elementsToMove * direction);
        }
        
        let targetOffset = this.baseOffset + (targetElementsMoved * this.itemWidth);
        if (!this.options.infinite) targetOffset = this.clampOffset(targetOffset);
        
        const distanceToTarget = targetOffset - this.currentOffset;
        
        if (Math.abs(distanceToTarget) < 5) {
            this.snapToPosition();
            return;
        }
        
        this.velocity = distanceToTarget * (1 - this.friction);
        this.isMomentumAnimating = true;
        this.track.style.transition = 'none';
        
        this.animateMomentum();
    }

    animateMomentum() {
        if (!this.isMomentumAnimating) return;
        
        const newOffset = this.currentOffset + this.velocity;
        this.currentOffset = this.clampOffset(newOffset);
        
        this.checkAndAddElements();
        this.updateTransform();
        
        this.velocity *= this.friction;
        
        if (Math.abs(this.velocity) < this.minVelocity) {
            this.stopMomentum();
            this.finalizePosition();
            return;
        }
        
        requestAnimationFrame(() => this.animateMomentum());
    }

    finalizePosition() {
        if (!this.options.infinite) {
            // KROK 1: Sprawdź czy jesteśmy blisko granic (PRZED snappingiem!)
            const boundaryTolerance = Math.abs(this.itemWidth * 0.3); // 30% szerokości slajda
            
            const distanceToEnd = Math.abs(this.currentOffset - this.minOffset);
            const distanceToStart = Math.abs(this.currentOffset - this.maxOffset);
            
            // Jeśli jesteśmy blisko końca/początku - idź tam bezpośrednio
            if (distanceToEnd < boundaryTolerance) {
                this.currentOffset = this.minOffset;
                this.baseOffset = this.currentOffset; // Resetuj baseOffset
                this.updateTransform();
                
                setTimeout(() => {
                    this.resumeAutoAnimation();
                    if (this.shouldResumeSlideshow && !this.isSlideshow) {
                        this.startSlideshow(this.slideshowDirection, this.slideshowInterval);
                    }
                }, 100);
                return; // WAŻNE: Wyjdź, nie rób normalnego snappingu!
            }
            
            if (distanceToStart < boundaryTolerance) {
                this.currentOffset = this.maxOffset;
                this.baseOffset = this.currentOffset; // Resetuj baseOffset  
                this.updateTransform();
                
                setTimeout(() => {
                    this.resumeAutoAnimation();
                    if (this.shouldResumeSlideshow && !this.isSlideshow) {
                        this.startSlideshow(this.slideshowDirection, this.slideshowInterval);
                    }
                }, 100);
                return; // WAŻNE: Wyjdź, nie rób normalnego snappingu!
            }
        }
        
        // KROK 2: Normalne snappowanie do elementów (tylko jeśli nie było boundary snap)
        const totalMove = this.currentOffset - this.baseOffset;
        const elementsMoved = Math.round(totalMove / this.itemWidth);
        this.baseOffset += elementsMoved * this.itemWidth;
        this.currentOffset = this.clampOffset(this.baseOffset);
        
        this.updateTransform();
        
        setTimeout(() => {
            this.resumeAutoAnimation();
            if (this.shouldResumeSlideshow && !this.isSlideshow) {
                this.startSlideshow(this.slideshowDirection, this.slideshowInterval);
            }
        }, 100);
    }
    
    snapToPosition() {
        if (!this.options.infinite) {
            // KROK 1: Sprawdź boundary snapping PRZED normalnym snappingiem
            const boundaryTolerance = Math.abs(this.itemWidth * 0.3);
            
            const distanceToEnd = Math.abs(this.currentOffset - this.minOffset);
            const distanceToStart = Math.abs(this.currentOffset - this.maxOffset);
            
            if (distanceToEnd < boundaryTolerance) {
                this.currentOffset = this.minOffset;
                this.baseOffset = this.currentOffset;
                
                this.track.style.transition = 'transform 0.3s ease-out';
                this.updateTransform();
                
                setTimeout(() => {
                    this.track.style.transition = 'none';
                    this.resumeAutoAnimation();
                    if (this.shouldResumeSlideshow && !this.isSlideshow) {
                        this.startSlideshow(this.slideshowDirection, this.slideshowInterval);
                    }
                }, 300);
                return; // Wyjdź, nie rób normalnego snappingu!
            }
            
            if (distanceToStart < boundaryTolerance) {
                this.currentOffset = this.maxOffset;
                this.baseOffset = this.currentOffset;
                
                this.track.style.transition = 'transform 0.3s ease-out';
                this.updateTransform();
                
                setTimeout(() => {
                    this.track.style.transition = 'none';
                    this.resumeAutoAnimation();
                    if (this.shouldResumeSlideshow && !this.isSlideshow) {
                        this.startSlideshow(this.slideshowDirection, this.slideshowInterval);
                    }
                }, 300);
                return; // Wyjdź, nie rób normalnego snappingu!
            }
        }
        
        // KROK 2: Normalne snappowanie (tylko jeśli nie było boundary snap)
        const totalMove = this.currentOffset - this.baseOffset;
        const elementsMoved = Math.round(totalMove / this.itemWidth);
        
        this.baseOffset += elementsMoved * this.itemWidth;
        this.currentOffset = this.clampOffset(this.baseOffset);
        
        this.track.style.transition = 'transform 0.3s ease-out';
        this.updateTransform();
        
        setTimeout(() => {
            this.track.style.transition = 'none';
            this.resumeAutoAnimation();
            if (this.shouldResumeSlideshow && !this.isSlideshow) {
                this.startSlideshow(this.slideshowDirection, this.slideshowInterval);
            }
        }, 300);
    }
    
    stopMomentum() {
        this.isMomentumAnimating = false;
        this.velocity = 0;
    }

    // DEBUG METHOD FOR TESTING
    debugDimensions() {
        const firstElement = this.originalItems[0];
        const computedStyle = window.getComputedStyle(firstElement);
        
        return {
            calculatedItemWidth: this.itemWidth,
            actualBoundingWidth: firstElement.getBoundingClientRect().width,
            offsetWidth: firstElement.offsetWidth,
            styleWidth: this.styleConfig.width,
            styleMaxWidth: this.styleConfig.maxWidth,
            computedWidth: computedStyle.width,
            computedMaxWidth: computedStyle.maxWidth,
            containerWidth: this.containerWidth,
            viewportWidth: window.innerWidth
        };
    }

    // PUBLIC API METHODS
    nextSlide() {
        this.nextSlideWithWrapAround();
    }

    prevSlide() {
        this.prevSlideWithWrapAround();
    }

    startAutoSlideshow(direction = 1, interval = this.slideshowInterval) {
        this.startSlideshow(direction, interval);
    }

    stopAutoSlideshow() {
        this.stopSlideshow();
    }

    startContinuousAnimation(direction = 1, speed = 50) {
        this.startAutoAnimation(direction, speed);
    }

    stopContinuousAnimation() {
        this.stopAutoAnimation();
    }

    setDirection(direction) {
        this.options.direction = direction;
    }

    setDirectionThreshold(threshold) {
        this.dragDirectionThreshold = threshold;
    }

    enableWheelScroll() {
        this.options.enableWheelScroll = true;
    }

    disableWheelScroll() {
        this.options.enableWheelScroll = false;
    }

    setWheelSensitivity(sensitivity) {
        this.wheelSensitivity = Math.max(0.1, Math.min(5.0, sensitivity));
    }

    setWheelThreshold(threshold) {
        this.wheelThreshold = Math.max(10, threshold);
    }

    setResizeDebounceTime(time) {
        this.resizeDebounceTime = Math.max(100, time); // minimum 100ms
    }

    setWrapAroundAnimation(duration = 500, easing = 'ease-out') {
        this.wrapAroundDuration = Math.max(100, duration); // minimum 100ms
        this.wrapAroundEasing = easing;
    }

    setReadingDirection(direction) {
        if (direction === 'ltr' || direction === 'rtl') {
            this.options.readingDirection = direction;
        }
    }

    getReadingDirection() {
        return this.options.readingDirection;
    }
}

// Initialize all sliders on the page
const sliders = document.querySelectorAll('.arcanov-slider');

sliders.forEach((slider, i) => {
    const sliderId = slider.getAttribute('slider-id');
    if (!sliderId) return;

    let options = {};
    
    if (slider.classList.contains('arcanov-slider--infinite-loop')) {
        options.infinite = true;
    }
    
    if (slider.classList.contains('arcanov-slider--moving-by-steps')) {
        options.steps = true;
    }
    
    if (slider.classList.contains('arcanov-slider--animating')) {
        options.animating = { x: i % 2 == 0 ? 0.5 : -0.5 };
        options.startPosition = i % 2 == 0 ? { x: -400 } : undefined;
    }

    const configData = slider.getAttribute('data-config');
    let config = {};
    if (configData) {
        try {
            config = JSON.parse(configData);
        } catch (e) {
            console.warn('Invalid slider config JSON:', configData);
        }
    }

    options.direction = 'horizontal';
    options.enableWheelScroll = !slider.classList.contains('arcanov-slider--no-wheel-scroll');

    const sliderInstance = new InfiniteSlider(slider, options);
    
    if (!window.arcanovSliders) {
        window.arcanovSliders = {};
    }
    window.arcanovSliders[sliderId] = sliderInstance;

    setTimeout(() => {
        if (config.autoSlideshow && config.autoSlideshow.enabled) {
            sliderInstance.startSlideshow(
                config.autoSlideshow.direction, 
                config.autoSlideshow.interval
            );
        } else if (config.autoAnimation && config.autoAnimation.enabled) {
            sliderInstance.startAutoAnimation(
                config.autoAnimation.direction, 
                config.autoAnimation.speed
            );
        }
    }, 500);
});